import React, { useState } from 'react'
import { KTSVG } from '../../../../../_metronic/helpers'

interface AISuggestionBoxProps {
  suggestion: string
  sentiment?: 'positive' | 'neutral' | 'negative'
  onAccept: (text: string) => void
  onDiscard: () => void
  onEdit: (text: string) => void
  isLoading?: boolean
}

/**
 * Componente para exibir sugestões de resposta geradas pela IA
 * Usado no modo "Assistido" do Concierge
 */
export const AISuggestionBox: React.FC<AISuggestionBoxProps> = ({
  suggestion,
  sentiment = 'neutral',
  onAccept,
  onDiscard,
  onEdit,
  isLoading = false
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editedText, setEditedText] = useState(suggestion)

  // Configuração de cores e ícones por sentimento
  const sentimentConfig = {
    positive: { 
      color: 'success', 
      icon: 'gen029', // smile
      label: 'Positivo',
      bgClass: 'bg-light-success'
    },
    neutral: { 
      color: 'info', 
      icon: 'gen045', // information
      label: 'Neutro',
      bgClass: 'bg-light-info'
    },
    negative: { 
      color: 'danger', 
      icon: 'gen044', // warning
      label: 'Negativo - Atenção!',
      bgClass: 'bg-light-danger'
    }
  }

  const config = sentimentConfig[sentiment]

  const handleAccept = () => {
    onAccept(editedText)
    setIsEditing(false)
  }

  const handleEdit = () => {
    if (isEditing) {
      onEdit(editedText)
      setIsEditing(false)
    } else {
      setIsEditing(true)
    }
  }

  const handleCancel = () => {
    setEditedText(suggestion) // Restaurar texto original
    setIsEditing(false)
  }

  return (
    <div className={`card border border-${config.color} shadow-sm mb-4 animate__animated animate__fadeInUp`}>
      {/* Header */}
      <div className={`card-header ${config.bgClass} py-3`}>
        <div className='d-flex align-items-center justify-content-between'>
          <div className='d-flex align-items-center'>
            <KTSVG
              path={`/media/icons/duotune/general/${config.icon}.svg`}
              className={`svg-icon-2 svg-icon-${config.color} me-2`}
            />
            <h5 className='mb-0 fw-bold'>
              <i className='fa fa-robot me-2'></i>
              Sugestão de Resposta (IA)
            </h5>
          </div>
          <span className={`badge badge-${config.color}`}>
            {config.label}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className='card-body'>
        {isLoading ? (
          <div className='text-center py-5'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Carregando sugestão...</span>
            </div>
            <p className='text-muted mt-3 mb-0'>Gerando sugestão de resposta...</p>
          </div>
        ) : (
          <>
            {!isEditing ? (
              <div className='bg-light p-4 rounded'>
                <p className='mb-0 text-gray-800' style={{ whiteSpace: 'pre-wrap' }}>
                  {editedText}
                </p>
              </div>
            ) : (
              <div>
                <label className='form-label fw-bold'>Editar Sugestão:</label>
                <textarea
                  className='form-control'
                  rows={6}
                  value={editedText}
                  onChange={(e) => setEditedText(e.target.value)}
                  placeholder='Edite a sugestão da IA...'
                  autoFocus
                />
              </div>
            )}

            {/* Alert para sentimento negativo */}
            {sentiment === 'negative' && (
              <div className='alert alert-danger d-flex align-items-center mt-4 mb-0'>
                <KTSVG
                  path='/media/icons/duotune/general/gen044.svg'
                  className='svg-icon-2hx svg-icon-danger me-3 flex-shrink-0'
                />
                <div>
                  <strong>Atenção!</strong> Mensagem do hóspede detectada como negativa. 
                  <br />
                  Considere uma resposta empática e personalizada.
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer com ações */}
      {!isLoading && (
        <div className='card-footer bg-light'>
          <div className='d-flex gap-2 justify-content-end'>
            {isEditing && (
              <button
                className='btn btn-light btn-sm'
                onClick={handleCancel}
              >
                <i className='fa fa-times me-1'></i>
                Cancelar
              </button>
            )}
            
            <button
              className='btn btn-light-danger btn-sm'
              onClick={onDiscard}
              disabled={isEditing}
            >
              <i className='fa fa-trash me-1'></i>
              Descartar
            </button>
            
            <button
              className='btn btn-light-warning btn-sm'
              onClick={handleEdit}
            >
              <i className='fa fa-edit me-1'></i>
              {isEditing ? 'Salvar Edição' : 'Editar'}
            </button>
            
            <button
              className='btn btn-success btn-sm'
              onClick={handleAccept}
            >
              <i className='fa fa-check me-1'></i>
              Aceitar e Enviar
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
